import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plus, Receipt, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatDayLabel, formatMoney, formatTime } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { useFeature } from "@/features/plan/hooks";
import { LockedButton } from "@/features/plan/Locked";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState, TableSkeleton } from "@/components/ui/states";
import { fetchOrders, type OrderRow } from "./api";
import {
  ORDER_FILTERS,
  fulfillmentLabel,
  orderStatus,
  paymentStatus,
  type OrderFilterId,
} from "./status";

const PAGE_SIZE = 25;

export function OrdersPage() {
  const { org } = useActiveOrg();
  const [filter, setFilter] = useState<OrderFilterId>("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);

  // Se busca al dejar de teclear, no en cada pulsacion: cada letra seria una
  // consulta con ILIKE sobre la tabla de pedidos.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => setPage(0), [filter, debounced, org?.organizationId]);

  const statuses = useMemo(
    () => ORDER_FILTERS.find((f) => f.id === filter)?.statuses ?? null,
    [filter],
  );

  const query = useQuery({
    queryKey: ["orders", org?.organizationId, filter, debounced, page],
    queryFn: () =>
      fetchOrders({
        organizationId: org!.organizationId,
        statuses,
        search: debounced,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: Boolean(org),
    placeholderData: keepPreviousData,
  });

  const timeZone = org?.timezone ?? "America/Lima";
  const currency = org?.currency ?? "PEN";

  // Vender desde el mostrador es punto de venta, y eso va con el plan Pro.
  const ventaManual = useFeature("sales.manual");

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-head font-semibold tracking-tight">Ventas</h1>
          <p className="mt-0.5 text-meta text-ink-2">
            {ventaManual
              ? "Todo lo que vendes, por la web y en el mostrador."
              : "Lo que entra por tu tienda web."}
          </p>
        </div>

        <div className="flex items-end gap-2">
          {/* La venta de mostrador es funcion de punto de venta y va con el
              plan Pro. El boton se enseña bloqueado en lugar de esconderlo:
              es la unica forma de que alguien pregunte por el. */}
          {ventaManual ? (
            <Button variant="primary" icon={<Plus className="size-3.5" />}>
              Nueva venta
            </Button>
          ) : (
            <LockedButton featureKey="sales.manual">Nueva venta</LockedButton>
          )}
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Número, cliente o teléfono"
            aria-label="Buscar pedidos"
            className="w-full rounded-md border border-line bg-surface py-1.5 pl-8 pr-3 text-ui sm:w-72"
          />
        </div>
      </header>

      <div className="flex flex-wrap gap-1.5">
        {ORDER_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              filter === f.id
                ? "border-ink bg-ink text-ground"
                : "border-line bg-surface text-ink-2 hover:bg-sunk",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Panel bleed>
        <QueryBoundary
          query={query}
          skeleton={<TableSkeleton rows={8} cols={6} />}
          isEmpty={(d) => d.rows.length === 0}
          empty={
            <EmptyState
              icon={<Receipt className="size-5" />}
              title={debounced || filter !== "all" ? "Ningún pedido coincide" : "Aún no hay pedidos"}
              description={
                debounced || filter !== "all"
                  ? "Prueba con otro filtro o quita la búsqueda."
                  : "Cuando entre el primer pedido de tu tienda, aparecerá aquí."
              }
              actions={
                (debounced || filter !== "all") && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setFilter("all");
                      setSearch("");
                    }}
                  >
                    Quitar filtros
                  </Button>
                )
              }
            />
          }
        >
          {(data) => (
            <>
              <OrdersTable rows={data.rows} timeZone={timeZone} currency={currency} />
              <OrdersCards rows={data.rows} timeZone={timeZone} currency={currency} />
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={data.total}
                onChange={setPage}
                busy={query.isFetching}
              />
            </>
          )}
        </QueryBoundary>
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------ tabla de escritorio */

function OrdersTable({
  rows,
  timeZone,
  currency,
}: {
  rows: OrderRow[];
  timeZone: string;
  currency: string;
}) {
  return (
    <div className="hidden overflow-x-auto md:block">
      <table className="w-full min-w-[720px] border-collapse text-ui">
        <thead>
          <tr>
            {["Pedido", "Cliente", "Entrega", "Total", "Pago", "Estado"].map((h, i) => (
              <th
                key={h}
                className={cn(
                  "border-b border-line bg-sunk px-4 py-2.5 font-mono text-mark font-medium uppercase tracking-[0.1em] text-ink-3",
                  i === 3 ? "text-right" : "text-left",
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const st = orderStatus(o.order_status, o.fulfillment_type);
            const pay = paymentStatus(o.payment_status);
            return (
              <tr key={o.id} className="border-b border-line-2 last:border-0 hover:bg-sunk/60">
                <td className="px-4 py-3">
                  <Link to={`/ventas/${o.id}`} className="tnum font-semibold hover:text-accent">
                    {o.order_number}
                  </Link>
                  <span className="tnum block text-xs text-ink-3">
                    {formatDayLabel(o.created_at, timeZone)} · {formatTime(o.created_at, timeZone)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-medium">{o.customer_name_snapshot}</span>
                  <span className="block text-xs text-ink-3">
                    {o.district ?? fulfillmentLabel(o.fulfillment_type)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusPill dot={false}>{fulfillmentLabel(o.fulfillment_type)}</StatusPill>
                </td>
                <td className="tnum px-4 py-3 text-right font-semibold">
                  {formatMoney(o.total, currency, { withSymbol: false })}
                </td>
                <td className="px-4 py-3">
                  <StatusPill tone={pay.tone}>{pay.label}</StatusPill>
                </td>
                <td className="px-4 py-3">
                  <StatusPill tone={st.tone}>{st.label}</StatusPill>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------------------------- tarjetas movil */

function OrdersCards({
  rows,
  timeZone,
  currency,
}: {
  rows: OrderRow[];
  timeZone: string;
  currency: string;
}) {
  return (
    <div className="flex flex-col gap-2 p-3 md:hidden">
      {rows.map((o) => {
        const st = orderStatus(o.order_status, o.fulfillment_type);
        const pay = paymentStatus(o.payment_status);
        return (
          <Link
            key={o.id}
            to={`/ventas/${o.id}`}
            className="block rounded-md border border-line bg-surface p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="tnum text-meta font-semibold">{o.order_number}</span>
              <StatusPill tone={st.tone}>{st.label}</StatusPill>
            </div>
            <p className="mt-1 text-ui">{o.customer_name_snapshot}</p>
            <p className="text-meta text-ink-3">
              {fulfillmentLabel(o.fulfillment_type)}
              {o.district ? ` · ${o.district}` : ""} · {formatTime(o.created_at, timeZone)}
            </p>
            <div className="mt-2 flex items-center justify-between gap-2">
              <StatusPill tone={pay.tone}>{pay.label}</StatusPill>
              <span className="tnum font-semibold">{formatMoney(o.total, currency)}</span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- paginacion */

function Pagination({
  page,
  pageSize,
  total,
  onChange,
  busy,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  busy: boolean;
}) {
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  const last = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <div className="flex items-center justify-between gap-3 border-t border-line-2 px-4 py-2.5">
      <span className={cn("tnum text-xs text-ink-3", busy && "opacity-50")}>
        {from}–{to} de {total}
      </span>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          disabled={page === 0}
          onClick={() => onChange(page - 1)}
          icon={<ChevronLeft className="size-3.5" />}
          aria-label="Página anterior"
        />
        <Button
          size="sm"
          variant="secondary"
          disabled={page >= last}
          onClick={() => onChange(page + 1)}
          icon={<ChevronRight className="size-3.5" />}
          aria-label="Página siguiente"
        />
      </div>
    </div>
  );
}
